import { useEffect, useState } from "react";
import { Eye, EyeOff, Lock, Mail } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import MatrixRainBackground from "@/components/auth/MatrixRainBackground";
import { useAuth } from "@/context/AuthContext";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isSubmitting } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage("");

    if (!email || !password) {
      setErrorMessage("Veuillez renseigner votre email et votre mot de passe.");
      return;
    }

    const result = await login(email.trim(), password);
    if (result.success) {
      navigate("/", { replace: true });
      return;
    }

    setErrorMessage(result.message || "Email ou mot de passe incorrect.");
  };

  return (
    <div className="login-page-bg login-matrix-shell flex min-h-screen items-center justify-center px-4 py-8" data-testid="login-page-container">
      <MatrixRainBackground />

      <div className="login-page-content w-full max-w-sm">
        <div className="mb-10 text-center">
          <img
            data-testid="login-logo"
            src="/logo-braux-white.png"
            alt="Braux Paysagistes"
            className="mx-auto h-20 w-auto"
          />
        </div>

        <div className="login-card w-full rounded-lg p-8">
          <div className="mb-8 text-center">
            <h1
              data-testid="login-title"
              className="font-['Playfair_Display'] text-3xl leading-tight text-white sm:text-4xl"
            >
              Administratif Finance & Stratégie
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            <div className="relative">
              <Mail className="pointer-events-none absolute left-4 top-4 h-6 w-6 text-zinc-500" />
              <Input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="Votre adresse email"
                autoComplete="email"
                className="h-14 border-zinc-800 bg-zinc-900/50 pl-12 text-white placeholder:text-zinc-600 focus-visible:border-white focus-visible:ring-0"
              />
            </div>

            <div className="relative">
              <Lock className="pointer-events-none absolute left-4 top-4 h-6 w-6 text-zinc-500" />
              <Input
                data-testid="login-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Votre mot de passe"
                autoComplete="current-password"
                className="h-14 border-zinc-800 bg-zinc-900/50 pl-12 pr-14 text-white placeholder:text-zinc-600 focus-visible:border-white focus-visible:ring-0"
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 transition-colors duration-200 hover:text-white"
                data-testid="login-password-visibility-toggle"
                aria-label={showPassword ? "Masquer le mot de passe" : "Afficher le mot de passe"}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {errorMessage ? (
              <p data-testid="login-error-message" className="text-sm text-rose-400">
                {errorMessage}
              </p>
            ) : null}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={isSubmitting}
              className="h-14 w-full rounded-sm bg-white text-base font-semibold text-black transition-transform duration-200 hover:scale-[1.01] hover:bg-zinc-200"
            >
              {isSubmitting ? "Connexion..." : "Se connecter"}
            </Button>

            <div className="text-center">
              <Link
                data-testid="forgot-password-link"
                to="/forgot-password"
                className="text-sm text-zinc-400 transition-colors duration-200 hover:text-white"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </form>

          <div className="mt-10 text-center text-xs text-zinc-500" data-testid="login-footer-text">
            <a
              data-testid="login-footer-nexus-link"
              href="https://nexus-conseil.ch/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex text-zinc-400 transition-colors duration-200 hover:text-zinc-200"
            >
              Codé avec ❤️ par NEXUS
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
